'use client';

import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import Base from '../../layouts/Base';
import HeadLine from '../../components/HeadLine/HeadLine';
import Flag from '../../components/Flag/Flag';
import TabArea from '../../components/TabArea/TabArea';
import ExportSelect from '../../components/ExportSelect/ExportSelect';
import Popover from '../../components/Popover/Popover';
import Dropdown from '../../components/Dropdown/Dropdown';
import Checkbox from '../../components/Checkbox/Checkbox';
import Button from '../../components/Button/Button';
import Icon from '../../components/Icon/Icon';
import { OverviewTab } from './OverviewTab';
import { NetworkTab, hasNetworkData } from './NetworkTab';
import { IndividualsTab } from './IndividualsTab';
import { useIndividualsFilters } from '../ConnectionsPage/hooks/useIndividualsFilters';
import useCompanyOrgChart from './useCompanyOrgChart';
import { t } from '../../i18n';
import s from './ConnectionsCompanyPage.module.scss';

const OVERVIEW_SLUG = 'overview';
const NETWORK_SLUG = 'network';
const INDIVIDUALS_SLUG = 'individual';

// Which tab an address names. Overview's address ends in the company's id, so
// anything that is not one of the two named tabs is it.
const tabFromPath = (pathname) => {
    const segment = pathname.replace(/\/+$/, '').split('/').pop();

    return segment === NETWORK_SLUG || segment === INDIVIDUALS_SLUG
        ? segment
        : OVERVIEW_SLUG;
};

const ConnectionsCompanyPage = ({
    name = '',
    companyCountry = null,
    activeHolding = {},
    individuals = [],
    orgChart = [],
    activeTab = OVERVIEW_SLUG,
    rootUrl = '',
    networkLastUpdated = '',
}) => {
    const [currentTab, setCurrentTab] = useState(activeTab);
    const chartExport = useRef(null);

    useEffect(() => setCurrentTab(activeTab), [activeTab]);

    // Each tab keeps its own address, written with pushState rather than a
    // router navigation: a navigation re-renders the page from the server and
    // mounts the tab afresh, which replays the revenue chart's entrance and
    // makes the org chart lay itself out a second time. Next keeps
    // usePathname in step with native history writes, so the addresses still
    // work when they are opened, reloaded or shared.
    const selectTab = (tab) => {
        setCurrentTab(tab);

        if (!rootUrl || !activeHolding.sourceId) {
            return;
        }

        const base = `${rootUrl}company/${activeHolding.sourceId}/`;

        window.history.pushState(
            null,
            '',
            tab === OVERVIEW_SLUG ? base : `${base}${tab}/`
        );
    };

    // Back and forward move between those addresses without a render of their
    // own, so the tab has to follow the history itself.
    useEffect(() => {
        const followHistory = () =>
            setCurrentTab(tabFromPath(window.location.pathname));

        window.addEventListener('popstate', followHistory);

        return () => window.removeEventListener('popstate', followHistory);
    }, []);

    // Fetched as soon as the page opens rather than when one of the two tabs
    // that need it is opened. The first org chart request for a client builds
    // the whole entity pool upstream — every company and every person the group
    // can see — which is slow enough to be worth spending while the reader is
    // still on Overview. It is cached server-side per holding and per group, so
    // a reader who never opens the tabs costs one request that the next visitor
    // to any company gets for free.
    const needsChart = !orgChart.length && !individuals.length;

    const chart = useCompanyOrgChart(
        needsChart ? activeHolding.sourceId : null,
        rootUrl
    );

    const chartNodes = orgChart.length ? orgChart : chart.orgChart;
    const allIndividuals = individuals.length ? individuals : chart.individuals;
    const filters = useIndividualsFilters(allIndividuals);

    // Filtering down to nothing must keep the controls, or there is no way back
    // — so these look at what arrived, not at what survived the filters.
    const canExportChart =
        !chart.isLoading &&
        hasNetworkData(chartNodes) &&
        currentTab === NETWORK_SLUG;
    const canFilterIndividuals =
        !chart.isLoading &&
        allIndividuals.length > 0 &&
        currentTab === INDIVIDUALS_SLUG;

    // Fenix says outright whether a company has a network, and it arrives with
    // the company itself — so the tab is closed from the first paint, as it is
    // on the old Connections page, rather than waiting on the chart and then
    // withdrawing a tab it had been offering. Nothing says as much about a
    // company's people, so Individuals stays open until there is something to
    // go on.
    const hasNetwork =
        typeof activeHolding.hasNetwork === 'boolean'
            ? activeHolding.hasNetwork
            : hasNetworkData(chartNodes);

    const tabItems = [
        {
            title: t('connectionsCompanyPage.titleOverview'),
            buttonId: OVERVIEW_SLUG,
        },
        {
            title: t('connectionsCompanyPage.titleNetwork'),
            buttonId: NETWORK_SLUG,
            isDisabled: !hasNetwork,
        },
        {
            title: t('connectionsCompanyPage.titleIndividuals'),
            buttonId: INDIVIDUALS_SLUG,
        },
    ];

    return (
        <div
            className={classNames(s.Root, {
                [s['Root--Network']]: currentTab === NETWORK_SLUG,
            })}>
            <section className={s.Content}>
                <div className={s.Title}>
                    <HeadLine title={name} />
                    {companyCountry && (
                        <div className={s.Country}>
                            <span className={s.CountryLabel}>
                                {companyCountry.label}
                            </span>
                            <Flag
                                countryCode={companyCountry.code}
                                width="3.2rem"
                            />
                        </div>
                    )}
                </div>
                <div className={s.TabRow}>
                    <TabArea
                        className={s.Tabs}
                        isLink={false}
                        label={t('connectionsCompanyPage.tabMenuLabel')}
                        items={tabItems}
                        activeItem={currentTab}
                        setActiveItem={selectTab}
                    />
                    {canFilterIndividuals && <IndividualsFilter {...filters} />}
                    {canExportChart && (
                        <ExportSelect
                            onPdf={() => chartExport.current?.downloadPdf()}
                            onImage={() => chartExport.current?.downloadImage()}
                        />
                    )}
                </div>
                <section className={s.Panel}>
                    {currentTab === OVERVIEW_SLUG && (
                        <OverviewTab holding={activeHolding} />
                    )}
                    {currentTab === NETWORK_SLUG && (
                        <NetworkTab
                            orgChart={chartNodes}
                            activeId={activeHolding.sourceId}
                            isLoading={chart.isLoading}
                            hasError={!!chart.error}
                            exportRef={chartExport}
                            fileName={name}
                            lastUpdated={networkLastUpdated}
                        />
                    )}
                    {currentTab === INDIVIDUALS_SLUG && (
                        <IndividualsTab
                            individuals={filters.filteredIndividuals}
                            isLoading={chart.isLoading}
                            hasError={!!chart.error}
                        />
                    )}
                </section>
            </section>
        </div>
    );
};

ConnectionsCompanyPage.propTypes = {
    name: PropTypes.string,
    companyCountry: PropTypes.object,
    activeHolding: PropTypes.object,
    individuals: PropTypes.array,
    orgChart: PropTypes.array,
    activeTab: PropTypes.string,
    rootUrl: PropTypes.string,
    networkLastUpdated: PropTypes.string,
};

const IndividualsFilter = ({
    politicalTypes = [],
    sectorTypes = [],
    selectedPolitics,
    setSelectedPolitics,
    selectedSectors,
    setSelectedSectors,
    searchTerm,
    setSearchTerm,
    activeFilters,
}) => {
    const popoverRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [openFacet, setOpenFacet] = useState(null);
    const [draft, setDraft] = useState(searchTerm);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                popoverRef.current &&
                !popoverRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () =>
            document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggle = (setter) => (value) =>
        setter((current) => ({ ...current, [value]: !current[value] }));

    const clearAll = () => {
        setSelectedPolitics({});
        setSelectedSectors({});
        setSearchTerm('');
        setDraft('');
    };

    const hasSelection =
        Object.values(selectedPolitics).some(Boolean) ||
        Object.values(selectedSectors).some(Boolean);

    const facets = [
        {
            title: t('connectionsCompanyPage.filterPolitics'),
            items: politicalTypes,
            selected: selectedPolitics,
            onChange: toggle(setSelectedPolitics),
        },
        {
            title: t('connectionsCompanyPage.filterSectors'),
            items: sectorTypes,
            selected: selectedSectors,
            onChange: toggle(setSelectedSectors),
        },
    ].filter((facet) => facet.items.length > 0);

    return (
        <div className={s.FilterRow}>
            {facets.length > 0 && (
                <Popover
                    minWidth="233px"
                    popoverRef={popoverRef}
                    modifiers={['Filter']}
                    isPopoverOpen={isOpen}
                    onClick={() => setIsOpen(!isOpen)}
                    content={
                        <div className={s.PopoverContent}>
                            {facets.map((facet) => (
                                <Dropdown
                                    key={facet.title}
                                    modifiers={['BorderBottom']}
                                    title={facet.title}
                                    isOpen={openFacet === facet.title}
                                    onToggle={(open) =>
                                        setOpenFacet(open ? facet.title : null)
                                    }
                                    showCheckmark={Object.values(
                                        facet.selected
                                    ).some(Boolean)}>
                                    <ul className={s.CheckboxList}>
                                        {facet.items.map((item) => (
                                            <li
                                                key={item.id}
                                                className={s.CheckboxItem}>
                                                <span
                                                    className={s.CheckboxLabel}>
                                                    {item.label}
                                                </span>
                                                <Checkbox
                                                    modifier="Alternative"
                                                    value={item.value}
                                                    checked={
                                                        !!facet.selected[
                                                            item.value
                                                        ]
                                                    }
                                                    onChange={() =>
                                                        facet.onChange(
                                                            item.value
                                                        )
                                                    }
                                                />
                                            </li>
                                        ))}
                                    </ul>
                                </Dropdown>
                            ))}
                            {hasSelection && (
                                <div className={s.FilterButtons}>
                                    <button
                                        className={s.ClearAllFilters}
                                        onClick={clearAll}>
                                        {t('mapFilter.clear')}
                                        <Icon
                                            name="Close"
                                            className={s.FilterIcon}
                                        />
                                    </button>
                                    <div className={s.FilterSeparator} />
                                    <button
                                        className={s.ClearAllFilters}
                                        onClick={() => setIsOpen(false)}>
                                        {t('mapFilter.done')}
                                    </button>
                                </div>
                            )}
                        </div>
                    }
                    overrideButton={
                        <Button
                            title={
                                isOpen
                                    ? t('mapFilter.close')
                                    : t('mapFilter.filter')
                            }
                            modifier="Filter"
                            className={s.FilterButton}
                            size="Small"
                            icon="FilterWhite"
                            isActive={isOpen}
                            isFullWidth
                            description={
                                activeFilters > 0 && `(${activeFilters})`
                            }
                        />
                    }
                />
            )}
            <div className={s.SearchWrapper}>
                <div className={s.SearchField}>
                    <Icon name="Search" className={s.SearchIcon} />
                    <input
                        className={s.SearchInput}
                        type="text"
                        placeholder={t('connectionsCompanyPage.searchPeople')}
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                        onKeyDown={(event) =>
                            event.key === 'Enter' && setSearchTerm(draft)
                        }
                    />
                    {draft && (
                        <button
                            className={s.ClearButton}
                            onClick={() => {
                                setDraft('');
                                setSearchTerm('');
                            }}>
                            <Icon name="CloseSmall" className={s.ClearIcon} />
                        </button>
                    )}
                </div>
                <button
                    className={s.SearchButton}
                    onClick={() => setSearchTerm(draft)}>
                    {t('mediaMonitorFilter.search')}
                </button>
            </div>
        </div>
    );
};

IndividualsFilter.propTypes = {
    politicalTypes: PropTypes.array,
    sectorTypes: PropTypes.array,
    selectedPolitics: PropTypes.object.isRequired,
    setSelectedPolitics: PropTypes.func.isRequired,
    selectedSectors: PropTypes.object.isRequired,
    setSelectedSectors: PropTypes.func.isRequired,
    searchTerm: PropTypes.string.isRequired,
    setSearchTerm: PropTypes.func.isRequired,
    activeFilters: PropTypes.number,
};

export const RawConnectionsCompanyPage = ConnectionsCompanyPage;
export default Base(ConnectionsCompanyPage);
